import React, { useEffect, useState } from 'react';
import { Route, Redirect } from 'react-router-dom';
import { inject, observer } from 'mobx-react';
import SubscriptionService from '../api/SubscriptionService';

/**
 * A wrapper for Route that redirects to the login page if user is not authenticated
 * and to subscription setup if they have a pending or canceled subscription
 */
const ProtectedRoute = ({ component: Component, store, ...rest }) => {
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  // Check subscription status on initial render
  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      if (store.isLoggedIn) {
        try {
          // Skip subscription check for subscription-related routes
          const path = window.location.pathname;
          const isSubscriptionRoute =
            path === '/student/subscriptions/setup' ||
            path === '/student/subscriptions/success' ||
            path === '/student/subscriptions/status' ||
            path === '/signup/success' ||
            path === '/signup/failed';

          if (isSubscriptionRoute) {
            setLoading(false);
            return;
          }

          // Check current subscription status
          const status = await SubscriptionService.getSubscriptionStatus();
          setSubscriptionStatus(status);
        } catch (error) {
          console.error('Error checking subscription status:', error);
        }
        setLoading(false);
      } else {
        setLoading(false);
      }
    };

    checkSubscriptionStatus();
  }, [store.isLoggedIn]);

  // If still loading, show nothing (could add a spinner here)
  if (loading) {
    return null;
  }

  return (
    <Route
      {...rest}
      render={props => {
        // If user is not logged in, redirect to login
        if (!store.isLoggedIn) {
          return (
            <Redirect
              to={{
                pathname: '/login',
                state: { from: props.location },
              }}
            />
          );
        }

        // If user has a pending setup from session storage, redirect to subscription setup
        const pendingSetupUser = sessionStorage.getItem('pendingSetupUser');
        if (
          pendingSetupUser &&
          props.location.pathname !== '/student/subscriptions/setup'
        ) {
          try {
            // Parse the pending setup info
            const setupInfo = JSON.parse(pendingSetupUser);

            // Check if it has the incomplete_setup status
            if (setupInfo.subscriptionStatus === 'incomplete_setup') {
              console.log(
                'ProtectedRoute: User has incomplete_setup status, redirecting to setup page'
              );
              return (
                <Redirect
                  to={{
                    pathname: '/student/subscriptions/setup',
                    state: { from: props.location },
                  }}
                />
              );
            }
          } catch (e) {
            console.error('Error parsing pendingSetupUser:', e);
            // If parsing fails, just redirect to be safe
            return (
              <Redirect
                to={{
                  pathname: '/student/subscriptions/setup',
                  state: { from: props.location },
                }}
              />
            );
          }
        }

        // Check store's subscription setup flag
        if (
          store.requiresSubscriptionSetup &&
          props.location.pathname !== '/student/subscriptions/setup'
        ) {
          return (
            <Redirect
              to={{
                pathname: '/student/subscriptions/setup',
                state: { from: props.location },
              }}
            />
          );
        }

        // Check subscription type and status
        if (
          subscriptionStatus &&
          subscriptionStatus.has_subscription === false &&
          subscriptionStatus.subscription_type === 'canceled' &&
          props.location.pathname !== '/student/subscriptions/status' &&
          props.location.pathname !== '/my-profile' &&
          props.location.pathname !== '/student/subscriptions/setup'
        ) {
          // We don't want to use async/await in the render method,
          // so we'll just store the fact that subscription is canceled
          // and let the SubscriptionManagement component handle the API call

          // Store the fact that subscription is canceled in session storage
          sessionStorage.setItem('canceledSubscription', 'true');

          // Redirect to subscription status page
          return (
            <Redirect
              to={{
                pathname: '/student/subscriptions/status',
                state: {
                  from: props.location,
                  message:
                    'Tu suscripción ha finalizado. Por favor, renueva para seguir utilizando la plataforma.',
                },
              }}
            />
          );
        }

        // Allow free users full access
        if (
          subscriptionStatus &&
          (subscriptionStatus.subscription_type === 'free_user' ||
            subscriptionStatus.subscription_type === 'paid')
        ) {
          // Free user or paid user has full access
          return <Component {...props} />;
        }

        // If all checks pass, render the component
        return <Component {...props} />;
      }}
    />
  );
};

export default inject('store')(observer(ProtectedRoute));
